import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaWhatsapp, FaEnvelope, FaPen, FaXmark, FaStar } from "react-icons/fa6";
import SearchablePicker, { SearchablePickerOption } from "../../organisms/SearchablePicker";
import MaskEdit from "../../organisms/MaskEdit";
import MultiEmailInput from "./MultiEmailInput";
import VariableChip from "./VariableChip";
import ContactModalForm from "./contact/ContactModalForm";
import ContactModalEdit from "./contact/ContactModalEdit";
import tokenAwareFetcher from "../../../utils/tokenAwareFetcher";
import appConfig from "../../../config/app-config";

const COMMUNICATION_ENV = appConfig.apis.communicationApiEnv;
const fetcher = tokenAwareFetcher(COMMUNICATION_ENV.address, COMMUNICATION_ENV.key);

export type Channel = "MAIL" | "WPP";

export interface EligiblePoint {
    id: string | null;
    type: string;
    value: string;
    valueFormatted: string;
    isActive: boolean;
    isPrimary: boolean;
    isWhatsapp?: boolean;
    notes?: string;
    templates?: string[];
    templateGroups: string[];
    shared: boolean;
    sharedContactPointId?: number;
    sharedWith?: Array<{ contactId: string; isActive: boolean }>;
    eligible: boolean;
    reason: "ok" | "inactive" | "other_group";
}

interface SelectedContact {
    id: string;
    name: string;
    taxId: string;
}

interface Props {
    channel: Channel;
    templateGroup?: string;
    onContactChange: (contactName: string) => void;
    onRecipientsChange: (values: string[]) => void;
}

const ContactRecipientPicker: React.FC<Props> = ({
    channel,
    templateGroup,
    onContactChange,
    onRecipientsChange,
}) => {
    const navigate = useNavigate();
    const [selected, setSelected] = useState<SelectedContact | null>(null);
    const [manualName, setManualName] = useState<string | null>(null);
    const [points, setPoints] = useState<EligiblePoint[]>([]);
    const [chosen, setChosen] = useState<Set<string>>(new Set());
    const [loadingPoints, setLoadingPoints] = useState(false);
    const [showCreateContact, setShowCreateContact] = useState(false);
    const [createSeed, setCreateSeed] = useState<string>("");
    const [editingContact, setEditingContact] = useState<{
        contactId: string;
        contactType: "PF" | "PJ";
        currentData: Record<string, unknown>;
    } | null>(null);

    const [manualEmails, setManualEmails] = useState<string[]>([]);
    const [manualPhone, setManualPhone] = useState("");

    const searchContacts = async (term: string): Promise<SearchablePickerOption[]> => {
        const params = new URLSearchParams({ q: term });
        const response = await fetcher.get("/contacts/search", params);
        if (!response.ok) return [];
        const { items } = await response.json();
        return (items || []).map((it: SelectedContact & { contactType: string }) => ({
            id: it.id,
            label: it.name,
            hint: it.taxId,
        }));
    };

    const loadEligiblePoints = async (contactId: string, preserveSelection = false) => {
        setLoadingPoints(true);
        try {
            const params = new URLSearchParams({ channel });
            if (templateGroup) params.set("templateGroup", templateGroup);
            const response = await fetcher.get(
                `/contacts/${contactId}/eligible-contact-points`,
                params,
            );
            if (!response.ok) {
                setPoints([]);
                return;
            }
            const { points: loaded } = await response.json();
            const list: EligiblePoint[] = loaded || [];
            setPoints(list);
            const eligibleValues = list.filter((p) => p.eligible).map((p) => p.value);
            const next = preserveSelection
                ? eligibleValues.filter((v) => chosen.has(v))
                : eligibleValues;
            setChosen(new Set(next));
            onRecipientsChange(next);
        } finally {
            setLoadingPoints(false);
        }
    };

    const resetRecipients = () => {
        setChosen(new Set());
        setManualEmails([]);
        setManualPhone("");
        onRecipientsChange([]);
    };

    const selectContact = (contact: SelectedContact) => {
        setManualName(null);
        setSelected(contact);
        resetRecipients();
        onContactChange(contact.name);
        loadEligiblePoints(contact.id);
    };

    const handleSelect = (option: SearchablePickerOption) => {
        selectContact({
            id: String(option.id),
            name: option.label,
            taxId: option.hint || "",
        });
    };

    const handleManualEntry = (name: string) => {
        setSelected(null);
        setPoints([]);
        setManualName(name);
        resetRecipients();
        onContactChange(name);
    };

    const handleClear = () => {
        setSelected(null);
        setManualName(null);
        setPoints([]);
        resetRecipients();
        onContactChange("");
    };

    const removeFromSelection = (value: string) => {
        const next = new Set(chosen);
        next.delete(value);
        setChosen(next);
        onRecipientsChange(Array.from(next));
    };

    const handleManualEmails = (emails: string[]) => {
        setManualEmails(emails);
        onRecipientsChange(emails);
    };

    const handleManualPhone = (phone: string) => {
        setManualPhone(phone);
        const digits = phone.replace(/\D/g, "");
        onRecipientsChange(digits ? [digits] : []);
    };

    const openEditContact = async () => {
        if (!selected) return;
        const response = await fetcher.get(`/contacts/${selected.id}/page-view`);
        if (!response.ok) return;
        const data = await response.json();
        setEditingContact({
            contactId: selected.id,
            contactType: data.contactType === "LEGAL_PERSON" ? "PJ" : "PF",
            currentData: {
                name: data.fullName,
                taxId: data.taxIdFormatted || data.taxId,
                taxRootId: data.taxRootId,
                observation: data.notes || "",
                farol: data.status || "",
                socialMedia: data.profile?.socialMedia || data.socialMedia,
                city: data.profile?.city || data.city,
                state: data.profile?.state || data.state,
                nickname: data.profile?.nickname,
                birthDate: data.profile?.birthDate,
                jobTitle: data.profile?.jobTitle,
                segment: data.profile?.segment,
                employees: data.profile?.employees,
                revenue: data.profile?.revenue,
                size: data.profile?.size,
                isHeadquarters: data.profile?.isHeadquarters,
            },
        });
    };

    const channelIcon =
        channel === "WPP" ? (
            <FaWhatsapp className="text-green-700" />
        ) : (
            <FaEnvelope className="text-blue-700" />
        );

    const eligiblePoints = points.filter((p) => p.eligible);

    const renderRecipientCard = () => {
        if (!selected && manualName === null) {
            return (
                <SearchablePicker
                    label="Destinatário:"
                    placeholder="Buscar por nome, CPF ou CNPJ"
                    minChars={3}
                    floatingResults
                    allowManualEntry
                    manualEntryLabel="Enviar para"
                    extraActionLabel="Cadastrar novo contato"
                    onSearch={searchContacts}
                    onSelect={handleSelect}
                    onManualEntry={handleManualEntry}
                    onExtraAction={(term) => {
                        setCreateSeed(term);
                        setShowCreateContact(true);
                    }}
                    onClear={handleClear}
                />
            );
        }

        return (
            <div className="rounded border border-gray-200 dark:border-slate-700">
                <span className="block px-3 pt-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                    Destinatário:
                </span>
                <div className="flex items-center justify-between gap-2 px-3 py-2">
                    <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-800 dark:text-gray-100">
                            {selected ? selected.name : manualName}
                        </p>
                        {selected ? (
                            selected.taxId && (
                                <button
                                    type="button"
                                    onClick={() =>
                                        navigate(`/communication/contact/${selected.id}/page-view`)
                                    }
                                    title="Abrir contato"
                                    className="font-mono text-xs text-blue-600 hover:underline dark:text-blue-400"
                                >
                                    {selected.taxId}
                                </button>
                            )
                        ) : (
                            <span className="text-xs text-gray-400">destinatário avulso</span>
                        )}
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                        {selected && (
                            <button
                                type="button"
                                onClick={openEditContact}
                                title="Editar contato"
                                className="rounded p-1.5 text-gray-400 hover:text-blue-600"
                            >
                                <FaPen size={12} />
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={handleClear}
                            title="Trocar destinatário"
                            className="rounded p-1.5 text-gray-400 hover:text-red-600"
                        >
                            <FaXmark size={14} />
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderContactPoints = () => {
        if (manualName !== null) {
            return (
                <div className="rounded border border-gray-200 dark:border-slate-700 p-3">
                    <span className="mb-2 flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                        {channelIcon}
                        {channel === "WPP" ? "Telefone" : "Emails"}
                    </span>
                    {channel === "MAIL" ? (
                        <MultiEmailInput
                            label=""
                            value={manualEmails}
                            onChange={handleManualEmails}
                            placeholder="Digite um email e tecle Enter"
                        />
                    ) : (
                        <MaskEdit
                            label=""
                            mask="(__) _____-____"
                            replacement={{ _: /\d/ }}
                            value={manualPhone}
                            onChange={(e) => handleManualPhone(e.target.value)}
                            prefix={<FaWhatsapp className="mr-2 text-green-500" />}
                        />
                    )}
                </div>
            );
        }

        if (!selected) {
            return (
                <div className="flex h-full items-center justify-center rounded border border-dashed border-gray-200 dark:border-slate-700 p-4 text-center text-xs text-gray-400">
                    Selecione um destinatário para escolher os pontos de contato.
                </div>
            );
        }

        return (
            <div className="rounded border border-gray-200 dark:border-slate-700 p-3">
                <span className="mb-2 flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                    {channelIcon}
                    {channel === "WPP" ? "Telefones" : "Emails"}
                </span>

                {loadingPoints ? (
                    <div className="flex justify-center py-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-blue-500" />
                    </div>
                ) : eligiblePoints.length === 0 ? (
                    <p className="py-1 text-xs text-gray-500 dark:text-gray-400">
                        Nenhum ponto de contato para este canal.
                    </p>
                ) : (
                    <div className="flex flex-wrap gap-2">
                        {eligiblePoints.map((point) => (
                            <VariableChip
                                key={point.value}
                                value={point.valueFormatted}
                                isVariable={false}
                                format={channel === "WPP" ? "phone" : "email"}
                                customIcon={
                                    point.isPrimary ? (
                                        <FaStar
                                            className="inline text-yellow-500"
                                            title="Principal"
                                        />
                                    ) : undefined
                                }
                                title={point.isPrimary ? "Principal" : undefined}
                                onRemove={() => removeFromSelection(point.value)}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-3">
            <div className="grid grid-cols-12 gap-4">
                <div className="col-span-5">
                    {renderRecipientCard()}
                    {templateGroup && (
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            Grupo do template:{" "}
                            <span className="font-medium text-gray-700 dark:text-gray-200">
                                {templateGroup}
                            </span>
                        </p>
                    )}
                </div>

                <div className="col-span-7">{renderContactPoints()}</div>
            </div>

            {showCreateContact && (
                <div className="fixed right-0 top-0 z-50 flex h-full w-full max-w-xl flex-col border-l border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-xl">
                    <div className="flex items-center justify-between border-b border-gray-200 dark:border-slate-700 px-6 py-3">
                        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                            Cadastrar novo contato
                        </h3>
                        <button
                            type="button"
                            onClick={() => setShowCreateContact(false)}
                            title="Fechar"
                            className="rounded p-1.5 text-gray-400 hover:text-red-600"
                        >
                            <FaXmark size={16} />
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-6">
                        <ContactModalForm
                            initialData={{ name: createSeed }}
                            onClose={() => setShowCreateContact(false)}
                            onSuccess={(newContact) => {
                                selectContact({
                                    id: String(newContact.id),
                                    name: newContact.fullName || newContact.name || "",
                                    taxId: newContact.taxIdFormatted || newContact.taxId || "",
                                });
                            }}
                        />
                    </div>
                </div>
            )}

            {editingContact && (
                <ContactModalEdit
                    contactId={editingContact.contactId}
                    contactType={editingContact.contactType}
                    currentData={editingContact.currentData as never}
                    onClose={() => setEditingContact(null)}
                    onSuccess={(updated) => {
                        setEditingContact(null);
                        if (selected) {
                            const name = updated?.fullName || selected.name;
                            setSelected({ ...selected, name });
                            onContactChange(name);
                            loadEligiblePoints(selected.id, true);
                        }
                    }}
                />
            )}
        </div>
    );
};

export default ContactRecipientPicker;
